import React, { useEffect, useState } from 'react';

// components
import ErrorBoundary from 'layout/components/ErrorBoundary';
import SlideCapture from 'onboarding/components/SlideCapture';
import SlideNotifications from 'onboarding/components/SlideNotifications';
import SlidePrivacy from 'onboarding/components/SlidePrivacy';

// helpers
import canAskForPush from 'pwa/helpers/canAskForPush';

// hooks
import usePushNotifications from 'pwa/hooks/usePushNotifications';
import { usePosthogCapture } from 'onboarding/posthogCapture/_index';

// styles
import * as Page from 'onboarding/styles/_index.style';

type Step = 'capture' | 'notifications' | 'privacy';

export default function OnboardingPage() {
  const [step, setStep] = useState<Step>('capture');
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState<boolean>(false);
  const onboarding = usePosthogCapture();
  const {
    status: pushStatus,
    isBusy: isEnablingPush,
    enable: enablePush,
  } = usePushNotifications();

  const canAskNow = canAskForPush(pushStatus);

  const leaveCapture = () => {
    onboarding.captureContinued();
    setStep(canAskNow ? 'notifications' : 'privacy');
  };

  const allowNotifications = async () => {
    onboarding.notificationsAllowed();
    await enablePush();
    setStep('privacy');
  };

  const skipNotifications = () => {
    onboarding.notificationsSkipped();
    setStep('privacy');
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isLearnMoreOpen) return;
      if (event.key === 'ArrowRight') {
        if (step === 'capture') leaveCapture();
        if (step === 'notifications' && !isEnablingPush) skipNotifications();
      }
      if (event.key === 'ArrowLeft') {
        if (step === 'notifications') setStep('capture');
        if (step === 'privacy')
          setStep(canAskNow ? 'notifications' : 'capture');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLearnMoreOpen, step, canAskNow, isEnablingPush]);

  return (
    <ErrorBoundary>
      <Page.Page>
        <Page.InnerPage>
          {step === 'capture' && <SlideCapture onContinue={leaveCapture} />}
          {step === 'notifications' && (
            <SlideNotifications
              isBusy={isEnablingPush}
              onAllow={allowNotifications}
              onSkip={skipNotifications}
            />
          )}
          {step === 'privacy' && (
            <SlidePrivacy
              isLearnMoreOpen={isLearnMoreOpen}
              setIsLearnMoreOpen={setIsLearnMoreOpen}
            />
          )}
        </Page.InnerPage>
      </Page.Page>
    </ErrorBoundary>
  );
}
